/**
 * Consumer feed filter — the pure half of Stage C (consumer-feed-filters spec
 * @ `Danielle-design/consumer-feed-filters`, tip ≥ 5fffd8f5; wire truth =
 * hydraedge `Danielle-feature/consumer-feed-filters-edge` @ 6926e11b and
 * hydracore `Danielle-feature/consumer-feed-filters-core` @ 275ddd64, which
 * agree field-for-field on everything this file depends on).
 *
 * Shared by BOTH transports — the SharedWorker (`hydraWorker.ts`) and
 * HydraClient's direct-WS path — and by the tab-side stores, so the three
 * places that must agree (what we send, what we admit, what we purge) read one
 * definition. Nothing here touches a socket, a port, or React.
 *
 * Three pieces:
 *
 *  1. The versioned `filter` object and the row predicate (`rowPassesFilter`)
 *     — the edge/core `connWantsMarket` semantics verbatim: axes AND;
 *     `tickers`/`sports` are allow-lists (a row must CARRY a matching value);
 *     `excludeClasses`/`excludeMarketTypes` are deny-lists with the A1
 *     per-row fail-open rule (a row lacking the field always passes). The
 *     client evaluates the SAME predicate on every filterReset to decide which
 *     cached rows are now out of scope — so a row the server would no longer
 *     send is never left authoritative locally.
 *
 *  2. `FeedFilterAggregator` — per-source needs → union → debounced diff with
 *     a narrowing grace, the SubscriptionAggregator pattern applied to the
 *     class/type/sports axes. Sources are OPT-INS: the connection starts from
 *     the browser defaults (props + futures excluded — ≈35–40% of rows, the
 *     torrent behind the 8/30 book freezes) and a view that renders props or
 *     futures declares the classes/types it needs, which REMOVES them from the
 *     exclusion set for as long as it is mounted (+ grace). Widening is
 *     immediate; narrowing waits out the grace so flipping between views that
 *     share a need does not churn replacement snapshots.
 *
 *  3. `FilterFence` — the A2′ client contract: install the ack'd
 *     (socketIncarnation, filterGeneration) pair, admit ONLY frames stamped
 *     with that exact pair, reset at every socket boundary. Stale-socket and
 *     stale-generation frames are dropped by construction.
 */

// ── Wire shapes ─────────────────────────────────────────────────────────────

/** The versioned markets-channel filter object exactly as sent on the wire
 *  (`{op:'subscribe', channel:'markets', filter}`). Presence = the COMPLETE
 *  filter from now on (replace, never union). An axis is OMITTED when it
 *  constrains nothing — the server REJECTS explicit empty arrays. */
export interface MarketFilter {
  tickers?: string[];
  sports?: string[];
  /** `canonicalGameKey` values — ONE event as an allow-list (games axis;
   *  hydracore e37e33d, hydraedge c93b18e). Either-key membership: a row
   *  passes if EITHER its legacy key or its doubleheader-safe V2 key is
   *  listed. A row carrying NEITHER fails, per A1's allow-list rule. */
  games?: string[];
  excludeClasses?: string[];
  excludeMarketTypes?: string[];
  cadenceMs?: number;
}

/** `filterAck` — answered for every filter-bearing subscribe, BEFORE any frame
 *  of the new generation. `effective` echoes the applied filter plus the
 *  effective cadence; on rejection `error` is set and the prior generation is
 *  retained (never a silent clear). `socketIncarnation` is server-minted and
 *  globally unique across reconnects/restarts — equality-compare only. */
export interface HydraFilterAckMsg {
  type: 'filterAck';
  channel: 'markets';
  socketIncarnation: string;
  filterGeneration: number;
  effective?: MarketFilterEffective;
  error?: string;
}

/** `marketRemoved` — the games-axis release gate (hydra review DNL-003).
 *  Sent on FILTERED connections only, when an ALREADY-DELIVERED row's identity
 *  change makes it fail the installed filter: re-keyed onto another game, or
 *  stripped keyless. It is the one case a filtered feed cannot express by
 *  omission — the consumer already holds the row, and no future frame would
 *  ever contradict it.
 *
 *  Stamped with the same pair every market frame carries, so it goes through
 *  the SAME fence: a removal stamped with anything but the installed pair is
 *  stale by construction and dropped, exactly like a stale market frame.
 *
 *  Filter CHANGES deliver no removals — the replacement snapshot after each
 *  filterAck re-establishes scope, and `applyFilterReset` purges what the new
 *  scope excludes. This message is only for identity changes inside a
 *  generation. */
export interface HydraMarketRemovedMsg {
  type: 'marketRemoved';
  ticker: string;
  socketIncarnation: string;
  filterGeneration: number;
  serverTs: number;
  seq?: number;
}

export interface MarketFilterEffective {
  tickers?: string[];
  sports?: string[];
  games?: string[];
  excludeClasses?: string[];
  excludeMarketTypes?: string[];
  cadenceMs: number;
  /** max(requested, pressureCadence) on the edge; the core floor additionally
   *  composes its pinned 250 ms producer coalesce (a request of 25 acks 250).
   *  Read THIS, never the request. */
  effectiveCadenceMs: number;
}

/** The fencing pair stamped on every markets frame of a filtered connection. */
export interface FencePair {
  socketIncarnation: string;
  filterGeneration: number;
}

/** Worker → tab (shared mode) / client → store (direct mode): ONE atomic reset
 *  per installed generation, emitted BEFORE any post-fence frame. Receivers
 *  drop every cached row that fails `scope` (evaluated with rowPassesFilter);
 *  the replacement snapshot that follows repopulates. `scope` is the SERVER's
 *  effective echo — the applied filter, not the request. */
export interface FilterResetMsg extends FencePair {
  type: '__filterReset';
  scope: MarketFilterEffective;
}

// ── Defaults ────────────────────────────────────────────────────────────────

/** Browser-class cadence (spec R4 consumer class 1: 250+; never lower). */
export const BROWSER_CADENCE_MS = 250;
/** The tickers allow-list sent when every mounted source is ticker-scoped
 *  and none has a row (a tracker with no positions, alone in its browser).
 *  Matches no engine ticker by construction, so the connection carries
 *  nothing instead of everything. See `buildMarketFilter`. */
export const NO_ROWS_TICKER = '__NO_ROWS__';
/** The warm-standby tier (Dan, 2026-09-02): the core floor connection carries
 *  every sport at the contract's ceiling while an edge owns what is on
 *  screen; it drops back to BROWSER_CADENCE_MS whenever it is the ONLY
 *  source for a viewed sport (edge down / not announcing). 1000 is the
 *  clamp maximum on both engines — longer needs a spec amendment. */
export const FLOOR_STANDBY_CADENCE_MS = 1000;

/** What a game-centric view never renders. `player-prop` is the class core
 *  stamps on every player ladder (the finer, core-owned authority — A1's
 *  reconciliation note: Kalshi prop rows carry marketType 'prop' only where
 *  edge parsers stamp it, so class is the reliable axis).
 *
 *  `future` is deliberately NOT excluded (g1.pm smoke, 2026-09-02, Dan:
 *  "futures are flat out missing from the dashboard"). The dashboard's
 *  Futures tab (`MARKET_TYPE_TABS` 'series' → `getSeriesEvents`) renders the
 *  Kalshi championship outcomes (`KXNBA-26-LAL`, …), which core files under
 *  `group: futures` and the edge therefore stamps `marketType:'future'` —
 *  the exclusion blanked that tab. The spec's exclude-by-default candidate
 *  was the UNKEYED Poly season futures, and `marketType` cannot separate
 *  those from the Kalshi boards g1 renders (no venue axis); a future
 *  `excludeClasses` value core stamps only on unrenderable rows would.
 *  Phase 1 is therefore player-props only. */
export const DEFAULT_EXCLUDED_CLASSES: readonly string[] = [
  // Dan, 2026-09-02: the front page is the bet-group toggles — Moneyline,
  // Spread/Total, Futures. Everything only GameDetail (or a props surface)
  // renders is trimmed across the board; those surfaces opt it back in
  // while mounted, and the account plane while it holds such a ticker.
  // Kept on the wire: moneyline, spread, total, advance, series*, futures.
  // NOT filterable here: period lines (1H/F5/quarters) — `period` is its
  // own axis and the filter grammar has no field for it (spec follow-up).
  'player-prop',
  'team-total', 'team-stat',
  'half-fulltime', 'overtime', 'two-point', 'first-to-score', 'first-td-team',
  'highest-quarter', 'every-quarter-btts', 'total-touchdowns', 'total-fieldgoals',
  'btts', 'rfi', 'extras',
  // NFL game props (hydracore nfl-game-lines-props, on main 2026-09-03):
  // GameDetail-only families, same rule.
  'race-to', 'both-teams-points',
];
export const DEFAULT_EXCLUDED_MARKET_TYPES: readonly string[] = [];
/** The classes a GAME DETAIL view renders beyond the front page — i.e. every
 *  default exclusion; it opts them all back in for its game's duration. */
export const GAME_DETAIL_CLASSES: readonly string[] = DEFAULT_EXCLUDED_CLASSES;

// ── Needs ───────────────────────────────────────────────────────────────────

/** What one source (a view, the account plane, a tab) needs from the feed.
 *  - `classes` / `marketTypes`: normally-excluded values this source renders —
 *    an OPT-IN that removes them from the exclusion set while declared.
 *  - `sports`: Phase 2 only. The sports this source renders (g1 `Sport` ids or
 *    hydra wire codes — normalized to uppercase wire codes here). `'all'` or
 *    ABSENT means the source needs every sport: a mounted source that does not
 *    say otherwise keeps the connection broad, so an undeclared view can never
 *    be starved by a sibling's narrower declaration. */
export interface FeedNeeds {
  /** Tickers axis (feed-filters-per-view, 2026-09-12). The EXACT rows this
   *  source renders — a tracker's held positions, a trader detail's book, a
   *  bot's configured pairs. A source that declares `tickers` is TICKER-
   *  SCOPED; one that does not is ticker-broad (it renders every row its
   *  other axes admit). The union puts the allow-list on the wire ONLY when
   *  every mounted source is ticker-scoped — an allow-list ANDs with every
   *  other axis, so beside a broad sibling (the dashboard in another tab of
   *  the same browser) it would starve that sibling. A ticker-scoped source
   *  therefore ALSO declares the fold of its rows (`sports`, `games`,
   *  `classes` — see `lib/feedFilter/tickerNeeds`): those are what carry
   *  its rows when the allow-list cannot ride. An explicit empty list is a
   *  ticker-scoped source with nothing to show (a tracker with no
   *  positions): when nothing else is mounted the wire says so with the
   *  `NO_ROWS_TICKER` sentinel instead of falling open to the firehose. */
  tickers?: Iterable<string>;
  classes?: Iterable<string>;
  marketTypes?: Iterable<string>;
  sports?: Iterable<string> | 'all';
  /** Games axis. Same rule as `sports`, one level narrower: `'all'` or ABSENT
   *  means this source needs EVERY game, so a mounted source that does not say
   *  otherwise keeps the connection broad and can never be starved by a
   *  sibling's one-event declaration. A source scoped to no game of its own
   *  (the account plane — its held tickers are marked from the core floor,
   *  which never carries this axis) declares the empty list. */
  games?: Iterable<string> | 'all';
}

/** The union across sources — the input to `buildMarketFilter`. */
export interface AggregatedNeeds {
  /** Exact tickers, or null = the union is ticker-broad (some source renders
   *  rows it cannot name). An EMPTY set is "nothing mounted names a row"
   *  (every source ticker-scoped and empty, or no source at all) — muted on
   *  the wire, never broad. */
  tickers: Set<string> | null;
  /** Classes that must NOT be excluded. */
  classes: Set<string>;
  /** Market types that must NOT be excluded. */
  marketTypes: Set<string>;
  /** Uppercase wire sport codes, or null = every sport (broad). */
  sports: Set<string> | null;
  /** canonicalGameKey values, or null = every game (broad). */
  games: Set<string> | null;
}

export function emptyNeeds(): AggregatedNeeds {
  return { tickers: null, classes: new Set(), marketTypes: new Set(), sports: null, games: null };
}

const normSport = (s: string): string => s.trim().toUpperCase();
const normLower = (s: string): string => s.trim().toLowerCase();

/** Reduce one source's declaration to the aggregator's key set. Keys are
 *  namespaced strings so one grace map covers every axis:
 *  `class:<c>`, `type:<t>`, `sport:<S>`, and `sport:*` for "every sport". */
export function needsToKeys(needs: FeedNeeds): Set<string> {
  const keys = new Set<string>();
  if (needs.tickers === undefined) {
    keys.add('ticker:*');
  } else {
    for (const t of needs.tickers) { const v = typeof t === 'string' ? t.trim() : ''; if (v) keys.add(`ticker:${v}`); }
  }
  for (const c of needs.classes ?? []) { const v = normLower(c); if (v) keys.add(`class:${v}`); }
  for (const t of needs.marketTypes ?? []) { const v = normLower(t); if (v) keys.add(`type:${v}`); }
  if (needs.games === undefined || needs.games === 'all') {
    keys.add('game:*');
  } else {
    // Keys are opaque (they contain '|', never ':'), so no normalization —
    // the engines compare them by exact string equality.
    for (const g of needs.games) { const v = typeof g === 'string' ? g.trim() : ''; if (v) keys.add(`game:${v}`); }
  }
  if (needs.sports === undefined || needs.sports === 'all') {
    keys.add('sport:*');
  } else {
    // An explicit (possibly empty) list adds exactly those sports — an empty
    // list is a source that needs no sport of its own (the account plane
    // with no positions), never "every sport".
    for (const s of needs.sports) { const v = normSport(s); if (v) keys.add(`sport:${v}`); }
  }
  return keys;
}

export function keysToNeeds(keys: Iterable<string>): AggregatedNeeds {
  const out = emptyNeeds();
  let allSports = false;
  let allGames = false;
  let allTickers = false;
  const sports = new Set<string>();
  const games = new Set<string>();
  const tickers = new Set<string>();
  for (const k of keys) {
    if (k === 'ticker:*') allTickers = true;
    else if (k.startsWith('ticker:')) tickers.add(k.slice(7));
    else if (k.startsWith('class:')) out.classes.add(k.slice(6));
    else if (k.startsWith('type:')) out.marketTypes.add(k.slice(5));
    else if (k === 'sport:*') allSports = true;
    else if (k.startsWith('sport:')) sports.add(k.slice(6));
    else if (k === 'game:*') allGames = true;
    else if (k.startsWith('game:')) games.add(k.slice(5));
  }
  out.sports = allSports ? null : sports;
  out.games = allGames ? null : games;
  // Ticker-broad the moment ANY source is (it renders rows it cannot name);
  // otherwise the exact union — which for an EMPTY key set is the empty set:
  // a tab with nothing mounted names no rows, and `buildMarketFilter` says so
  // on the wire instead of pulling the firehose (the floor still carries
  // every sport at its standby cadence, so nothing can go dark).
  out.tickers = allTickers ? null : tickers;
  return out;
}

/** Build the wire filter from aggregated needs.
 *
 *  - Exclusions = defaults minus what any source needs. An emptied axis is
 *    OMITTED (explicit `[]` is rejected by the server).
 *  - `sports` rides only when the Phase-2 axis is on, and it is the viewed
 *    sports VERBATIM on every sport-narrowed connection — an edge that does
 *    not serve any of them answers with nothing, which is the point: the
 *    core floor carries its sports at the standby cadence. (The first cut
 *    intersected the list with each edge's static claim scope and fell back
 *    to BROAD on an empty intersection; the g1.pm spike showed he1 — tennis
 *    — still streaming 330 KB/s on the NFL tab because of it.) `feedSports`
 *    is kept for callers but no longer restricts.
 *  - `cadenceMs` is always present: browsers negotiate 250 explicitly. */
export function buildMarketFilter(
  needs: AggregatedNeeds,
  opts: { cadenceMs?: number; sportsAxis?: boolean; gamesAxis?: boolean; tickersAxis?: boolean; feedSports?: ReadonlySet<string> | null } = {},
): MarketFilter {
  const filter: MarketFilter = {};
  // Tickers axis: when every mounted source names its rows, the allow-list
  // IS the filter. Nothing else rides with it — an allow-list of exact rows
  // makes the sport/game axes redundant, and a deny-list beside it could only
  // drop a row the source asked for by name (a held player-prop position).
  // Engines reject an explicit `[]`, and omitting the axis would mean the
  // firehose, so "these sources have no rows" is said with a sentinel that
  // matches nothing: the connection is narrow, acked, and quiet.
  if (opts.tickersAxis && needs.tickers != null) {
    filter.tickers = needs.tickers.size > 0 ? [...needs.tickers].sort() : [NO_ROWS_TICKER];
    filter.cadenceMs = opts.cadenceMs ?? BROWSER_CADENCE_MS;
    return filter;
  }
  const excludeClasses = DEFAULT_EXCLUDED_CLASSES.filter((c) => !needs.classes.has(c));
  if (excludeClasses.length > 0) filter.excludeClasses = excludeClasses;
  const excludeMarketTypes = DEFAULT_EXCLUDED_MARKET_TYPES.filter((t) => !needs.marketTypes.has(t));
  if (excludeMarketTypes.length > 0) filter.excludeMarketTypes = excludeMarketTypes;
  if (opts.sportsAxis && needs.sports != null) {
    const wanted = [...needs.sports];
    if (wanted.length > 0) filter.sports = wanted.sort();
  }
  if (opts.gamesAxis && needs.games != null) {
    const wanted = [...needs.games];
    if (wanted.length > 0) filter.games = wanted.sort();
  }
  filter.cadenceMs = opts.cadenceMs ?? BROWSER_CADENCE_MS;
  return filter;
}

/** Canonical string for change detection — two filters with the same axes in
 *  any order compare equal. */
export function filterKey(filter: MarketFilter): string {
  const sorted = (a?: string[]): string[] | undefined => (a ? [...a].sort() : undefined);
  return JSON.stringify({
    tickers: sorted(filter.tickers),
    sports: sorted(filter.sports),
    games: sorted(filter.games),
    excludeClasses: sorted(filter.excludeClasses),
    excludeMarketTypes: sorted(filter.excludeMarketTypes),
    cadenceMs: filter.cadenceMs,
  });
}

// ── Row predicate ───────────────────────────────────────────────────────────

/** The row fields the predicate reads. A market frame's `market` object, a
 *  store record, and a worker-cache entry all satisfy this structurally. */
export interface FilterableRow {
  ticker: string;
  sport?: string | null;
  class?: string | null;
  marketType?: string | null;
  /** Either-key membership for the games axis; both optional on the wire. */
  canonicalGameKey?: string | null;
  canonicalGameKeyV2?: string | null;
}

/** Scope in any of its shapes (the wire request, the ack's `effective` echo,
 *  or a reset's `scope`) — arrays, as serialized. */
export type FilterScope = Pick<MarketFilter, 'tickers' | 'sports' | 'games' | 'excludeClasses' | 'excludeMarketTypes'>;

/** Row-vs-filter eligibility — the edge's `connWantsMarket` / core's
 *  `rowPassesFilter` semantics, verbatim: axes AND; allow-lists require a
 *  matching value; deny-lists are per-row fail-open (a row with no `class`
 *  passes every `excludeClasses`; no `marketType` passes every
 *  `excludeMarketTypes`). Case folding matches the server's (sports upper,
 *  class/type lower) so a locally-stamped row and a wire row agree. */
export function rowPassesFilter(scope: FilterScope, row: FilterableRow): boolean {
  if (scope.tickers && scope.tickers.length > 0 && !scope.tickers.includes(row.ticker)) return false;
  if (scope.sports && scope.sports.length > 0) {
    const s = row.sport ? normSport(row.sport) : null;
    if (!s || !scope.sports.some((x) => normSport(x) === s)) return false;
  }
  if (scope.games && scope.games.length > 0) {
    // Either-key membership, byte-for-byte with core's `rowInGames`: V2 is the
    // stricter doubleheader-safe key, and the legacy key keeps dhAmbiguous
    // rows (which carry NO V2 by contract) addressable. A row with neither key
    // is not this game's row — allow-lists require the field to be CARRIED.
    const legacy = row.canonicalGameKey ?? null;
    const v2 = row.canonicalGameKeyV2 ?? null;
    if (!legacy && !v2) return false;
    if (!scope.games.some((g) => g === legacy || g === v2)) return false;
  }
  if (scope.excludeClasses && scope.excludeClasses.length > 0 && row.class) {
    const c = normLower(row.class);
    if (scope.excludeClasses.some((x) => normLower(x) === c)) return false;
  }
  if (scope.excludeMarketTypes && scope.excludeMarketTypes.length > 0 && row.marketType) {
    const t = normLower(row.marketType);
    if (scope.excludeMarketTypes.some((x) => normLower(x) === t)) return false;
  }
  return true;
}

// ── Aggregator ──────────────────────────────────────────────────────────────

export interface FeedFilterAggregatorTimers {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

export interface FeedFilterAggregatorOptions {
  /** Grace before a need that no source still declares is actually retired
   *  (the connection narrows). Defaults to 30 s — the orderbook aggregator's
   *  unsubscribe grace, for the same reason: quick view flips must not churn
   *  replacement snapshots. */
  narrowingGraceMs?: number;
  /** Coalesce window for change emission. A route change mounts several
   *  sources in one tick; one filter (one ack, one snapshot) should result.
   *  Defaults to 50 ms. `0` emits synchronously. */
  emitDebounceMs?: number;
  timers?: FeedFilterAggregatorTimers;
}

/**
 * Per-source needs → effective union (live ∪ retiring) → `onChange` with the
 * new AggregatedNeeds whenever the effective set changes. Transport-agnostic:
 * the worker feeds it per-port declarations, the direct-WS client feeds it the
 * tab's single declaration, the tab-side registry feeds it per-view ones.
 */
export class FeedFilterAggregator<SourceId> {
  private perSource = new Map<SourceId, Set<string>>();
  /** Keys no live source declares any more, held through the grace. */
  private retiring = new Map<string, unknown>();
  private lastEmittedKeys: string | null = null;
  private emitHandle: unknown = null;
  private readonly graceMs: number;
  private readonly debounceMs: number;
  private readonly timers: FeedFilterAggregatorTimers;

  constructor(
    private readonly onChange: (needs: AggregatedNeeds) => void,
    opts: FeedFilterAggregatorOptions = {},
  ) {
    this.graceMs = opts.narrowingGraceMs ?? 30_000;
    this.debounceMs = opts.emitDebounceMs ?? 50;
    this.timers = opts.timers ?? {
      setTimeout: (fn, ms) => setTimeout(fn, ms),
      clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
    };
  }

  /** Replace a source's declaration. `null` drops the source. */
  setNeeds(source: SourceId, needs: FeedNeeds | null): void {
    if (needs == null) this.perSource.delete(source);
    else this.perSource.set(source, needsToKeys(needs));
    this.reconcile();
  }

  removeSource(source: SourceId): void {
    this.perSource.delete(source);
    this.reconcile();
  }

  hasSource(source: SourceId): boolean {
    return this.perSource.has(source);
  }

  /** Live union (what sources declare right now, no grace). */
  liveKeys(): Set<string> {
    const out = new Set<string>();
    for (const keys of this.perSource.values()) for (const k of keys) out.add(k);
    return out;
  }

  /** Effective union = live ∪ retiring — what the wire filter is built from. */
  effectiveKeys(): Set<string> {
    const out = this.liveKeys();
    for (const k of this.retiring.keys()) out.add(k);
    return out;
  }

  current(): AggregatedNeeds {
    return keysToNeeds(this.effectiveKeys());
  }

  /** Re-run the diff; called by every mutation. Widening (a key newly live)
   *  emits now (debounced); narrowing (a key no longer live) starts its grace
   *  timer and emits when it fires — unless the key comes back first. */
  reconcile(): void {
    const live = this.liveKeys();
    for (const k of live) {
      const h = this.retiring.get(k);
      if (h !== undefined) { this.timers.clearTimeout(h); this.retiring.delete(k); }
    }
    // graceMs 0 = no grace at all: dropped keys leave synchronously (the tab
    // registry — the transports own the real grace).
    const previous = this.graceMs <= 0 || this.lastEmittedKeys == null
      ? new Set<string>()
      : new Set(this.lastEmittedKeys.split(' ').filter(Boolean));
    for (const k of previous) {
      if (live.has(k) || this.retiring.has(k)) continue;
      const handle = this.timers.setTimeout(() => {
        this.retiring.delete(k);
        if (this.liveKeys().has(k)) return; // re-declared during the grace
        this.scheduleEmit();
      }, this.graceMs);
      this.retiring.set(k, handle);
    }
    this.scheduleEmit();
  }

  /** Forget the grace timers and force the next reconcile to emit whatever
   *  the live union is (a socket boundary: the new socket re-sends the
   *  complete filter anyway, so retiring keys can be dropped or kept — we
   *  keep them so a reconnect mid-grace does not narrow early). */
  flushPending(): void {
    if (this.emitHandle != null) { this.timers.clearTimeout(this.emitHandle); this.emitHandle = null; }
    this.emitNow();
  }

  private scheduleEmit(): void {
    if (this.debounceMs <= 0) { this.emitNow(); return; }
    if (this.emitHandle != null) return;
    this.emitHandle = this.timers.setTimeout(() => { this.emitHandle = null; this.emitNow(); }, this.debounceMs);
  }

  private emitNow(): void {
    const keys = [...this.effectiveKeys()].sort().join(' ');
    if (keys === this.lastEmittedKeys) return;
    this.lastEmittedKeys = keys;
    this.onChange(keysToNeeds(keys.split(' ').filter(Boolean)));
  }
}

// ── Fence ───────────────────────────────────────────────────────────────────

export type FenceAckResult =
  | { kind: 'installed'; pair: FencePair; effective: MarketFilterEffective }
  | { kind: 'rejected'; error: string }
  | { kind: 'ignored'; reason: 'stale-incarnation' | 'not-newer' };

/**
 * A2′ client fencing for ONE connection. Exactly the four wire steps: the
 * server validates and accepts; emits `filterAck` before any frame of that
 * generation; the client installs the exact pair (and performs its reset);
 * only frames stamped with that exact pair are admissible.
 *
 * Socket boundaries call `reset()` — nothing is admitted on a new socket until
 * its own ack arrives, and a frame from a previous socket can never match
 * (incarnations are globally unique by construction).
 */
export class FilterFence {
  private installed: FencePair | null = null;
  /** The incarnation of the socket we currently hold an ack from — a
   *  late ack from an older socket (any other incarnation, once one is
   *  installed) is ignored. */
  private incarnation: string | null = null;

  get pair(): FencePair | null { return this.installed; }

  /** Socket boundary (open / close / reconnect): forget everything. */
  reset(): void {
    this.installed = null;
    this.incarnation = null;
  }

  onAck(ack: HydraFilterAckMsg): FenceAckResult {
    if (typeof ack.socketIncarnation !== 'string' || typeof ack.filterGeneration !== 'number') {
      return { kind: 'rejected', error: 'malformed_ack' };
    }
    if (this.incarnation != null && ack.socketIncarnation !== this.incarnation) {
      return { kind: 'ignored', reason: 'stale-incarnation' };
    }
    if (ack.error || !ack.effective) {
      // Rejection: the server retained the prior filter + generation, so the
      // installed pair (if any) stays authoritative. A rejected FIRST filter
      // installs nothing — and the server sends nothing, so nothing is
      // admitted either.
      return { kind: 'rejected', error: ack.error ?? 'invalid_filter' };
    }
    if (this.installed && this.installed.socketIncarnation === ack.socketIncarnation
      && ack.filterGeneration <= this.installed.filterGeneration) {
      return { kind: 'ignored', reason: 'not-newer' };
    }
    this.incarnation = ack.socketIncarnation;
    this.installed = { socketIncarnation: ack.socketIncarnation, filterGeneration: ack.filterGeneration };
    return { kind: 'installed', pair: { ...this.installed }, effective: ack.effective };
  }

  /** Exact-pair admission. Unstamped frames are NOT admissible on a fenced
   *  connection (a filtered connection stamps every markets frame; an
   *  unstamped one can only be a legacy-path frame that does not belong). */
  admits(frame: { socketIncarnation?: unknown; filterGeneration?: unknown }): boolean {
    const p = this.installed;
    if (!p) return false;
    return frame.socketIncarnation === p.socketIncarnation && frame.filterGeneration === p.filterGeneration;
  }
}
